// Change requests from the auftrag portal (AB4, docs/schnittstelle-adressbuch.md 6): which fields a customer may ask to
// change, where they are stored and how they are labelled, and the check of the changes a request brings.

export type ChangeTarget = "firma" | "person" | "adresse";
export type ChangeDecision = "angenommen" | "abgelehnt";

export interface ChangeField {
  /** The column of companies, contacts or company_addresses. */
  column: string;
  label: string;
  kind?: "required" | "email" | "vat" | "art";
}

/** auftrag's field names (as in the read API, the main address as anschrift.<feld>) per target. */
export const CHANGE_FIELDS: Record<ChangeTarget, Record<string, ChangeField>> = {
  firma: {
    name: { column: "name", label: "Name", kind: "required" },
    "anschrift.zusatz": { column: "address_extra", label: "Zusatz" },
    "anschrift.strasse": { column: "street", label: "Straße" },
    "anschrift.plz": { column: "zip", label: "PLZ" },
    "anschrift.ort": { column: "city", label: "Ort" },
    "anschrift.land": { column: "country", label: "Land" },
    ust_idnr: { column: "vat_id", label: "USt-IdNr.", kind: "vat" },
    handelsregister: { column: "commercial_register", label: "Handelsregister" },
    telefon: { column: "phone", label: "Telefon" },
    email: { column: "email", label: "Mail", kind: "email" },
    website: { column: "website", label: "Website" },
  },
  person: {
    anrede: { column: "salutation", label: "Anrede" },
    name: { column: "name", label: "Name" },
    email: { column: "email", label: "Mail", kind: "email" },
    telefon: { column: "phone", label: "Telefon" },
    mobil: { column: "mobile", label: "Mobil" },
  },
  adresse: {
    art: { column: "kind", label: "Art", kind: "art" },
    bezeichnung: { column: "label", label: "Bezeichnung" },
    zusatz: { column: "extra", label: "Zusatz" },
    strasse: { column: "street", label: "Straße" },
    plz: { column: "zip", label: "PLZ" },
    ort: { column: "city", label: "Ort" },
    land: { column: "country", label: "Land" },
  },
};

/** What a new person or address must bring. */
export const NEW_RECORD_FIELDS: Record<Exclude<ChangeTarget, "firma">, string[]> = { person: ["name"], adresse: ["art", "strasse", "plz", "ort"] };

export const ADDRESS_ARTEN = ["rechnung", "lieferung", "weitere"] as const;

const NOUN: Record<ChangeTarget, string> = { firma: "eine Firma", person: "eine Person", adresse: "eine Adresse" };
const MAX = 300;

export type ChangeCheck = { ok: true; changes: Record<string, string | null> } | { ok: false; feld: string | null; message: string };

/**
 * The requested changes, checked and normalised: text or null (empty is null), the USt-IdNr. without spaces in upper
 * case. `anschrift` may also come as an object; it is read as anschrift.<feld>.
 */
export function readChanges(target: ChangeTarget, raw: Record<string, unknown>, creates: boolean): ChangeCheck {
  const bad = (feld: string | null, message: string): ChangeCheck => ({ ok: false, feld, message });
  const entries = Object.entries(raw).flatMap(([k, v]): [string, unknown][] =>
    target === "firma" && k === "anschrift" && v && typeof v === "object" && !Array.isArray(v) ? Object.entries(v).map(([s, w]) => [`anschrift.${s}`, w]) : [[k, v]],
  );
  const defs = CHANGE_FIELDS[target];
  const changes: Record<string, string | null> = {};
  for (const [feld, v] of entries) {
    const def = Object.hasOwn(defs, feld) ? defs[feld]! : null;
    if (!def) return bad(feld, `Das Feld „${feld}“ lässt sich für ${NOUN[target]} nicht ändern.`);
    if (v !== null && typeof v !== "string") return bad(feld, `Feld „${feld}“: Text oder null erwartet.`);
    let value = v?.trim() || null;
    if (value && value.length > MAX) return bad(feld, `Feld „${feld}“: höchstens ${MAX} Zeichen.`);
    if (def.kind === "vat" && value) value = value.replace(/\s+/g, "").toUpperCase();
    if (def.kind === "email" && value && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) return bad(feld, `Feld „${feld}“: keine gültige Mailadresse.`);
    if (def.kind === "art" && !ADDRESS_ARTEN.includes(value as (typeof ADDRESS_ARTEN)[number])) return bad(feld, `Feld „${feld}“: rechnung, lieferung oder weitere erwartet.`);
    if (def.kind === "required" && !value) return bad(feld, `Feld „${feld}“ darf nicht leer sein.`);
    changes[feld] = value;
  }
  if (!Object.keys(changes).length) return bad("aenderungen", "Der Antrag enthält keine Änderung.");
  if (creates && target !== "firma") {
    const missing = NEW_RECORD_FIELDS[target].find((f) => !changes[f]);
    if (missing) return bad(missing, `Für ${target === "person" ? "eine neue Person" : "eine neue Adresse"} fehlt das Feld „${missing}“.`);
  }
  return { ok: true, changes };
}

/** The fields of a record as auftrag names them, from its row (missing columns are null). */
export function fieldValues(target: ChangeTarget, row: Record<string, unknown> | null, fields: string[]): Record<string, string | null> {
  return Object.fromEntries(fields.map((f) => [f, row ? ((row[CHANGE_FIELDS[target][f]!.column] as string | null | undefined) ?? null) : null]));
}
